import React, { useEffect } from 'react';
import {useLocation} from "react-router-dom";
import { useAsync } from 'react-async-hook';
import { useTable, Column, useSortBy, HeaderGroup } from 'react-table';
import ReactPaginate from 'react-paginate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile } from '@fortawesome/free-solid-svg-icons';
import { formatOmittedDateTime } from '../libs/utils/formatDate';
import useLoading from '../hooks/useLoading';
import { getRoomattachfileIndex } from '../apis/getRoomattachfileIndex';
import { CreateRoomAttachFileModal } from '../components/pages/RoomAttachFile/CreateRoomAttachFileModal';
import icon06 from '../assets/images/ROOM_TaskFile_Re_06.png';
import icon11 from '../assets/images/ROOM_TaskFile_Re_11.png';
import icon14 from '../assets/images/ROOM_TaskFile_Re_14.png';
import icon16 from '../assets/images/ROOM_TaskFile_Re_16.png';
import icon22 from '../assets/images/ROOM_TaskFile_Re_22.png';
import icon23 from '../assets/images/ROOM_TaskFile_Re_23.png';
import icon26 from '../assets/images/ROOM_TaskFile_Re_26.png';
import icon28 from '../assets/images/ROOM_TaskFile_Re_28.png';
import icon442 from '../assets/images/ROOM_TaskBoard_Re4_42.png';
import { FileData, RoomAttachFile } from '../types/types';
import { DeleteRoomAttachFileModal } from '../components/pages/RoomAttachFile/DeleteRoomAttachFileModal';
import { useFileSearch } from '../hooks/useFileSearch';
import { config } from '../libs/config/config';
import {NoDataMessage} from "../components/pages/common/table/NoDataMessage";
import useAppRouter from "../hooks/useAppRouter";
import {useAuth} from "../hooks/useAuth";
import {logError} from "../libs/utils/logError";
import {menuMaterialApi} from "../apis/admin/menu/menuMaterialApi";
import {LazyAndPreloadImage} from "../components/pages/common/image/LazyAndPreloadImage";

const columns: Column<FileData>[] = [
  {
    Header: 'サムネイル',
    accessor: 'fileURL',
    disableSortBy: true,
  },
  {
    Header: 'ファイル名',
    accessor: 'name',
    disableSortBy: true,
  },
  {
    Header: 'タスク名',
    accessor: 'taskName',
    disableSortBy: true,
  },
  {
    Header: '作成日時',
    accessor: 'createdAt',
  },
  {
    Header: '作成者',
    accessor: 'createUser',
    disableSortBy: true,
  },
  {
    Header: 'サイズ',
    accessor: 'size',
  },
];

interface TableComponentProps {
  data: FileData[];
  reloadFiles: () => Promise<void>;
  setRequestParams: React.Dispatch<React.SetStateAction<RequestParams>>;
  resetPagination: () => void;
}

const TableComponent = (dataProps: TableComponentProps) => {
  const { goto } = useAppRouter();
  const { state: { currentUser } } = useAuth();
  if(currentUser?.accessRoomIds.every( id => id !== currentUser?.roomId)) {
    goto('forbiddenError');
  }
  const { data, reloadFiles, setRequestParams, resetPagination } = dataProps;
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setSortBy,
    state: { sortBy },
  } = useTable({ columns, data, manualSortBy: true }, useSortBy);
  const [file, setFile] = React.useState<FileData | undefined>();
  const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false);
  // memo カラムidが入る・配列の順番が優先順位（早い方が優先度高）
  const [sortPriority, setSortPriority] = React.useState<string[]>([]);

  useEffect(() => {
    const onSort = async () => {
      if (sortBy.length === 0) {
        setRequestParams((prevState) => ({ ...prevState, sort: [] }));
        resetPagination();
      }
      const sortParams: string[] = sortBy.map(
        (s) => `${s.id}:${s.desc ? 'desc' : 'asc'}`
      );
      setRequestParams((prevState) => ({ ...prevState, sort: sortParams }));
      resetPagination();
    };

    onSort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, setRequestParams]);

  const handleSort = (column: HeaderGroup<FileData>) => {
    // columnsでdisableSortBy: trueにしているものは弾く
    if (!column.canSort) {
      return;
    }

    if (!sortPriority.includes(column.id)) {
      setSortPriority((prevState) => [...prevState, column.id]);
    }

    // ソートをクリックするたび、desc → asc → ソート無し となるようにするため、以下の条件分岐が必要
    if (!column.isSorted) {
      setSortBy([...sortBy, { id: column.id, desc: true }]);
      return;
    }
    if (column.isSortedDesc) {
      setSortBy(
        sortBy.map((s) =>
          s.id === column.id ? { id: column.id, desc: false } : s
        )
      );
    } else {
      setSortBy(sortBy.filter((s) => s.id !== column.id));
      setSortPriority((prevState) =>
        prevState.filter((sp) => sp !== column.id)
      );
    }
  };

  const handleDeleteClick = (fileData: FileData) => {
    setFile(fileData);
    setShowDeleteModal(true);
  };

  const handleDownloadClick = async(fileData: FileData) => {
    try {
      const response = await menuMaterialApi.downloadFile(
        { path: fileData.fileURL },
        {
          responseType:'blob',
          dataType:'binary'
        });

      const downloadUrl = window.URL.createObjectURL(new Blob([response]));
      const downloadLink = document.createElement("a");
      document.body.appendChild(downloadLink);
      downloadLink.download = decodeURIComponent(`${fileData.name}.${fileData.ext}`);
      downloadLink.href = downloadUrl;
      downloadLink.click();
      downloadLink.remove();
      URL.revokeObjectURL(downloadUrl);

    } catch(e) {
      goto('internalServerError');
    }
  };

  const onDeleteSubmitted = async () => {
    await reloadFiles();
    setShowDeleteModal(false);
  };

  const fileSizeUnit = (fileSize: number) => {
    // 1 KB = 1024 Byte
    const unit = {
      kb: 1024,
      mb: 1024 ** 2,
      gb: 1024 ** 3,
      tb: 1024 ** 4,
      pb: 1024 ** 5,
    };

    const round = (size: number, key: string) => {
      return Math.round(size / unit[key as keyof typeof unit]);
    };

    if (fileSize >= unit.pb) {
      return `${round(fileSize, 'pb')} PB`;
    }
    if (fileSize >= unit.tb) {
      return `${round(fileSize, 'tb')} TB`;
    }
    if (fileSize >= unit.gb) {
      return `${round(fileSize, 'gb')} GB`;
    }
    if (fileSize >= unit.mb) {
      return `${round(fileSize, 'mb')} MB`;
    }
    if (fileSize >= unit.kb) {
      return `${round(fileSize, 'kb')} KB`;
    }
    return `${fileSize} B`;
  };

  return (
    <>
      {showDeleteModal && file && (
        <DeleteRoomAttachFileModal
          {...{ file, onSubmitted: onDeleteSubmitted }}
        />
      )}
      <div className="overflow-x-scroll whitespace-nowrap block">
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <table {...getTableProps()} className="file-table">
          <thead className="bg-primary text-white text-xs w-full">
            {headerGroups.map((headerGroup) => (
              // eslint-disable-next-line
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  // eslint-disable-next-line
                  <th
                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    onClick={() => handleSort(column)}
                  >
                    <div className="px-8 py-1 flex items-center justify-center relative">
                      {column.render('Header')}
                      <div className="absolute right-1 flex items-center">
                        {column.canSort && !column.isSorted && (
                          <img src={icon06} alt="icon" />
                        )}
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <>
                              <img src={icon22} alt="icon" />
                              <div className="text-white text-xs">
                                {sortPriority.indexOf(column.id) + 1}
                              </div>
                            </>
                          ) : (
                            <>
                              <img src={icon23} alt="icon" />
                              <div className="text-white text-xs">
                                {sortPriority.indexOf(column.id) + 1}
                              </div>
                            </>
                          )
                        ) : (
                          ''
                        )}
                      </div>
                    </div>
                  </th>
                ))}
                <th className="py-1">ファイル操作</th>
              </tr>
            ))}
          </thead>
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <tbody className="bg-white text-sm" {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                // eslint-disable-next-line
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) =>
                    cell.column.id === 'fileURL' ? (
                      // eslint-disable-next-line react/jsx-key
                      <td
                        className="p-4 flex items-center justify-center"
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...cell.getCellProps()}
                      >
                        <div className="w-12 h-12 overflow-hidden">
                          {row.original.mimeType.includes('image') ? (
                            <LazyAndPreloadImage
                              {...{
                                path: cell.value
                              }}
                            />
                          ) : (
                            <div className="h-full w-full flex justify-center items-center">
                              <FontAwesomeIcon
                                icon={faFile}
                                size="2x"
                                className="text-gray-500"
                              />
                            </div>
                          )}
                        </div>
                      </td>
                    ) : (
                      // eslint-disable-next-line react/jsx-key
                      <td
                        className={`p-4 w-1 ${
                          ['name', 'taskName'].includes(cell.column.id) &&
                          'font-medium w-4/12 whitespace-normal'
                        }
                        `}
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...cell.getCellProps()}
                      >
                        {cell.column.id === 'createUser' ? (
                          <div className="flex items-center w-40 whitespace-normal">
                            <img src={icon442} alt="icon" className="mr-2" />
                            {cell.render('Cell')}
                          </div>
                        ) : (
                          <>
                            {/* eslint-disable-next-line no-nested-ternary */}
                            {cell.column.id === 'createdAt'
                              ? formatOmittedDateTime(cell.value)
                              : cell.column.id === 'size'
                              ? fileSizeUnit(cell.value)
                              : cell.render('Cell')}
                          </>
                        )}
                      </td>
                    )
                  )}
                  <td className="px-4 py-1">
                    <div className="flex items-center w-28">
                      <button
                        type="button"
                        onClick={() => handleDeleteClick(row.original)}
                        className="mx-2 outline-none focus:outline-none"
                      >
                        <img src={icon26} alt="icon" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownloadClick(row.original)}
                        className="mx-2 outline-none focus:outline-none"
                      >
                        <img src={icon28} alt="icon" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        { data.length === 0 && (
          <div className="mt-6">
            <NoDataMessage />
          </div>
        )}
      </div>
    </>
  );
};

interface SelectOptions {
  value: number;
  label: string;
}

const options: SelectOptions[] = config.list.pageSizeOptions.map((s) => ({
  value: s,
  label: String(s),
}));

interface RequestParams {
  keyword: string;
  from?: number;
  size: number;
  sort?: string[];
}

export const RoomAttachFilePage: React.FC = () => {
  const [files, setFiles] = React.useState<FileData[]>([]);
  const [totalHits, setTotalHits] = React.useState<number>(0);
  const [pageNumber, setPageNumber] = React.useState<number>(0);
  const [requestParams, setRequestParams] = React.useState<RequestParams>({
    keyword: '',
    size: 10,
  });
  const withLoading = useLoading();
  const location = useLocation();
  const { goto } = useAppRouter();

  const {
    keyword,
    isSubmitted,
    onSubmitted: onSearchSubmitted,
  } = useFileSearch();
  const [showCreateModal, setShowCreateModal] = React.useState<boolean>(false);

  const resetPagination = () => {
    setPageNumber(0);
    setRequestParams((prevState) => ({ ...prevState, from: 0 }));
  };

  React.useEffect(() => {
    if (isSubmitted) {
      setRequestParams((prevState) => ({ ...prevState, keyword }));
      resetPagination();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitted]);

  const { execute: reloadFiles } = useAsync(async () => {
    const fetch = async () => {
      withLoading(async () => {
        try {
          const response = await getRoomattachfileIndex(requestParams);
          setFiles(
            response.data.taskFileList.map((tl: RoomAttachFile) => ({
              id: tl.id,
              fileURL: tl.fileURL,
              name: tl.name,
              taskName: tl.taskName,
              createdAt: tl.createDateTime,
              createUser: tl.createUser,
              size: tl.size,
              mimeType: tl.mimeType,
              ext: tl.ext,
            }))
          );
          setTotalHits(response.data.totalHits);
        } catch(e) {
          await logError(
            location.pathname,
            'タスクファイル一覧の読み込みに失敗しました。',
            e
          );

          goto('internalServerError');
          return;
        } finally {
          onSearchSubmitted();
        }
      });
    };
    fetch();
  }, [requestParams]);

  const onSubmitted = async () => {
    await reloadFiles();
    setShowCreateModal(false);
  };

  const handlePaginate = (selectedItem: { selected: number }) => {
    // selectedは0スタート
    const from = requestParams.size * selectedItem.selected;
    setPageNumber(selectedItem.selected);
    setRequestParams((prevState) => ({ ...prevState, from }));
  };

  const calculatePageCount = () => {
    if (totalHits === 0) {
      return 1;
    }
    return Math.ceil(totalHits / requestParams.size);
  };

  const handleSizeChange = (value: number) => {
    setRequestParams((prevState) => ({ ...prevState, size: value }));
    resetPagination();
  };

  return (
    <>
      {showCreateModal && <CreateRoomAttachFileModal {...{ onSubmitted }} />}
      <div className="bg-sub p-8 min-h-screen">
        <div className="container mx-auto">
          <div className="flex justify-between mb-2">
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="ml-4 flex items-center text-primary font-semibold text-sm outline-none focus:outline-none"
            >
              <img src={icon11} alt="icon" className="mr-2" />
              ファイルをアップロード
            </button>
            <div className="flex items-center text-primary text-sm font-semibold">
              <div className="pr-4 mr-4 border-r border-primary">
                {`total: ${totalHits}`}
              </div>
              <div className="pr-4 mr-4 border-r border-primary">
                <span>Number of Preview : </span>
                <select
                  className="pagination-select"
                  value={requestParams.size}
                  onChange={(e) => {
                    handleSizeChange(parseInt(e.target.value, 10));
                  }}
                >
                  {options.map(option => (
                    <option key={option.label} value={option.value}>
                      {option.value}
                    </option>
                  ))}
                </select>
              </div>

              <ReactPaginate
                pageCount={calculatePageCount()}
                marginPagesDisplayed={1}
                pageRangeDisplayed={5}
                onPageChange={handlePaginate}
                forcePage={pageNumber}
                previousLabel={
                  <div className="flex items-center">
                    <img src={icon14} alt="icon" className="mr-2" />
                    Prev
                  </div>
                }
                nextLabel={
                  <div className="flex items-center">
                    Next
                    <img src={icon16} alt="icon" className="ml-2" />
                  </div>
                }
                containerClassName="flex items-center space-x-2 text-primary text-sm font-semibold"
                activeClassName="bg-primary text-white w-4 h-4 flex items-center justify-center"
              />
            </div>
          </div>
          <TableComponent
            {...{ data: files, reloadFiles, setRequestParams, resetPagination }}
          />
        </div>
      </div>
    </>
  );
};
